import makeStyles from '@mui/styles/makeStyles';

import { AppAvatar, brandclub_colors } from '@stackline/ui';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { FC } from 'react';
import { withRouter } from 'react-router-dom';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SelectRetailerDropdown from 'src/components/BeaconV2Components/Header/SelectRetailerDropdown';
import SearchInput from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { useBeaconHomeLink } from 'src/components/BeaconV2Components/hooks/useBeaconHomeLink';
import { BEACON_PRO_CENTER_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import DisputeStatusUpdater from 'src/components/ShortageDisputes/components/DisputeManagement/DisputeStatusUpdater';
import { BeaconLogo } from 'src/components/SvgIcons/SvgIcons';
import { onShortagesMode } from 'src/store/modules/user/selectors';
import { useAppSelector } from 'src/utils/Hooks';
import { useAppSubscribed } from 'src/utils/Hooks/reduxSelectors';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { GlobalComponents } from 'src/components/BeaconV2Components/Header/GlobalComponents/GlobalComponents';
import AccountDivisionPopover from 'src/components/BeaconV2Components/Header/AccountDivisionPopover';
import { shouldShowBeaconAdvisor } from 'src/utils/app';

const useStyles = makeStyles({
  header: {
    height: 60,
    backgroundColor: brandclub_colors.blueWhite,
    position: 'initial',
    top: 0,
    zIndex: 1000
  },
  centerDiv: {
    margin: '0  auto',
    width: BEACON_PRO_CENTER_WIDTH,
    boxSizing: 'border-box',
    height: 60,
    display: 'grid',
    gridTemplateColumns: `1fr 330px 1fr`,
    alignItems: 'center'
  },
  productLogo: {
    height: 30,
    width: 30,
    marginLeft: '-1px',
    '& :hover': {
      cursor: 'pointer'
    }
  },
  userOptionsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px'
  },
  companyName: {
    fontSize: '1.4rem',
    fontWeight: 500,
    lineHeight: 1.43,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  settingsIconButton: {
    height: 24,
    width: 24,
    padding: 0,
    margin: '0 26px 0 20px',
    borderRight: '1ps solid black'
  },
  accountIconButton: {
    height: 30,
    width: 30,
    padding: 0
  },
  accountIcon: {
    height: 36,
    width: 36
  },
  // drawer
  drawerButtonContainer: {
    textAlign: 'right'
  },
  drawerButtonRoot: {
    paddingRight: 0
  },
  menuIcon: {
    transform: 'scale(1.4, 1.7)'
  },
  menuCloseIcon: {
    transform: 'scale(1.5)'
  },
  drawerRoot: {
    fontFamily: 'Roboto'
  },
  drawerPaperRoot: {
    top: 60,
    left: 0,
    padding: 20
  },
  drawerCompanyName: {
    color: brandclub_colors.darkBlue,
    fontSize: '1.8rem',
    fontWeight: 'bold',
    lineHeight: 1.43,
    marginTop: 10,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  listRoot: {
    color: brandclub_colors.darkBlue
  },
  listItemRoot: {
    padding: '20px 0',
    '& a': {
      color: brandclub_colors.darkBlue,
      textDecoration: 'none'
    }
  }
});

// -------------------------------------------------------------

interface HeaderProps {
  location: any;
  history: any;
}

const Header: FC = ({ location, history }: HeaderProps) => {
  const classes = useStyles();
  const beaconHomeLink = useBeaconHomeLink();
  const app = useAppSelector((state) => state.app);
  const showAdvisor = shouldShowBeaconAdvisor();

  const APP_DETAILS = useAppSubscribed();

  const appDropdownOption = Object.keys(APP_DETAILS)
    .map((appName) => {
      return {
        ...APP_DETAILS[appName],
        label: APP_DETAILS[appName].getLabel(),
        disabled: !APP_DETAILS[appName].shouldBeEnabled()
      };
    })
    .filter((option) => {
      return option.id !== 'omni' && option.id !== 'drive';
    });

  return (
    <div className={classes.header} style={showAdvisor ? { position: 'relative' } : undefined}>
      <div className={classes.centerDiv} style={showAdvisor ? { gridTemplateColumns: '1fr 600px 1fr' } : undefined}>
        <Flex alignItems="center">
          <GlobalComponents />
          <AppAvatar
            variant="square"
            sx={{ backgroundColor: 'transparent' }}
            classes={{
              root: classes.productLogo
            }}
            imgProps={{
              style: {
                objectFit: 'contain'
              }
            }}
            onClick={() => {
              if (`${location.pathname + location.search}` !== beaconHomeLink) {
                history.push(beaconHomeLink);
              }
            }}
          >
            <BeaconLogo />
          </AppAvatar>

          <SlDropdownMenu
            options={appDropdownOption}
            defaultLabel="Beacon"
            selectedId="beacon"
            onChange={(selectedApp: SlDropdownMenuOption & { path: string }) => {
              if (selectedApp.id !== 'beacon') {
                window.open(selectedApp.path, '_blank');
              }
            }}
            buttonVariant="titleSmall"
            iconStyle={{ marginLeft: '8px' }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            menuStyles={{ width: '151px' }}
            slMenuProps={{
              fixSelectedId: false,
              sx: {
                '& .MuiMenuItem-root': {
                  paddingRight: '12px'
                }
              }
            }}
          />
        </Flex>

        {showAdvisor ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SearchInput />
          </div>
        ) : (
          <SearchInput />
        )}
        <div>
          <div className={classes.userOptionsDiv}>
            <SelectRetailerDropdown />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                  <AccountDivisionPopover appName="beacon" />
                  {onShortagesMode() && app.name === 'beacon' ? <DisputeStatusUpdater /> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(Header);
